'use client';

import { Divider, Stack, Typography } from '@mui/material';
import type {
  InventoryResource,
  SecurityScanResult,
  SecurityScanResultEvidenceTypeEnum,
  SecurityScansSummaryResponse,
} from '@repo/api-gw-sdk';
import { Fragment } from 'react';

import { dayjs, DATE_FORMAT } from '@/utils/dayjs';

import { AnomalyChart } from './anomalyChart';
import { formatEvidenceSentence, getEvidenceStrings } from './securityUtils';

import { StatusCardHeader } from '../panels/instance/tabs/overview-tab/statusCardHeader';
import { StatusCardWrapper } from '../panels/instance/tabs/overview-tab/statusCardWrapper';

interface DataAnomalyCardProps {
  resource: InventoryResource;
  scans: SecurityScansSummaryResponse;
}

export const DataAnomalyCard = ({ resource, scans }: DataAnomalyCardProps) => {
  const results: SecurityScanResult[] = scans?.lastScan?.results || [];
  const groupedEvidences = results.reduce<
    Map<SecurityScanResultEvidenceTypeEnum, SecurityScanResult[]>
  >((map, r) => {
    const key = r.evidenceType;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
    return map;
  }, new Map());

  return (
    <StatusCardWrapper borderColor='var(--mui-palette-warning-main)'>
      <Stack gap='24px'>
        <Stack marginBottom='12px' alignItems='center' spacing='12px'>
          <Stack width='100%' padding='0 24px 0 40px'>
            <StatusCardHeader
              title='Data Anomaly Detected'
              actions={[]}
              entity={resource}
            />
          </Stack>
          <Typography className='font-semibold' fontSize='16px'>
            Anomalous data patterns detected
          </Typography>
        </Stack>
        <Typography paddingX='40px' variant='subtitle1'>
          {`Anomaly indicators from the latest snapshot on ${dayjs.utc(scans?.lastScan?.createdTime).format(DATE_FORMAT)}:`}
        </Typography>
        <Stack marginY='12px' gap='16px'>
          {[...groupedEvidences.entries()].map(
            ([evidenceType, groupResults], idx) => {
              const evidenceStrings = getEvidenceStrings(evidenceType);
              const count = groupResults.length;
              const graphResult = groupResults.find((r) => r.anomalyGraph);
              return (
                <Fragment key={evidenceType}>
                  {!!idx && <Divider />}
                  <Stack paddingX='40px' gap='8px'>
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      alignItems='center'
                    >
                      <Typography variant='h4'>
                        {evidenceStrings.label}
                      </Typography>
                      <Typography variant='h4'>{count}</Typography>
                    </Stack>
                    <Typography variant='subtitle1'>
                      {formatEvidenceSentence(count, evidenceStrings)}
                    </Typography>
                    {graphResult?.anomalyGraph && (
                      <AnomalyChart
                        graphData={graphResult.anomalyGraph}
                        label={evidenceStrings.label}
                      />
                    )}
                  </Stack>
                </Fragment>
              );
            }
          )}
        </Stack>
      </Stack>
    </StatusCardWrapper>
  );
};
